import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Loader2, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import { Switch } from "@/components/ui/switch";

interface BatchResult {
  url: string;
  status: 'pending' | 'processing' | 'success' | 'error';
  data?: any;
  error?: string;
}

interface BatchBlogGeneratorProps {
  onBlogGenerated: (data: any) => void;
}

export function BatchBlogGenerator({ onBlogGenerated }: BatchBlogGeneratorProps) {
  const [urls, setUrls] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<BatchResult[]>([]);
  const [generateImages, setGenerateImages] = useState(true);
  const { toast } = useToast();

  const handleBatchGenerate = async () => {
    const urlList = urls
      .split("\n")
      .map((url) => url.trim())
      .filter((url) => url.length > 0);

    if (urlList.length === 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter at least one YouTube URL",
      });
      return;
    }

    if (urlList.length > 10) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Maximum 10 URLs can be processed at once",
      });
      return;
    }

    setIsProcessing(true);
    setResults(urlList.map((url) => ({ url, status: 'pending' })));

    for (let i = 0; i < urlList.length; i++) {
      const url = urlList[i];
      
      // Update status to processing
      setResults((prev) =>
        prev.map((result, idx) =>
          idx === i ? { ...result, status: 'processing' } : result
        )
      );

      try {
        const { data, error } = await supabase.functions.invoke(
          'generate-blog-from-video',
          {
            body: { videoUrl: url, generateImage: generateImages },
          }
        );

        if (error) throw error;
        if (data.error) throw new Error(data.error);

        // Update status to success
        setResults((prev) =>
          prev.map((result, idx) =>
            idx === i ? { ...result, status: 'success', data } : result
          )
        );

        toast({
          title: "Success",
          description: `Blog ${i + 1}/${urlList.length} generated successfully`,
        });
      } catch (error: any) {
        console.error('Error generating blog:', error);
        
        // Update status to error
        setResults((prev) =>
          prev.map((result, idx) =>
            idx === i
              ? { ...result, status: 'error', error: error.message }
              : result
          )
        );

        toast({
          variant: "destructive",
          title: "Error",
          description: `Failed to generate blog ${i + 1}: ${error.message}`,
        });
      }

      // Add a small delay between requests to avoid rate limiting
      if (i < urlList.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }

    setIsProcessing(false);
    
    toast({
      title: "Batch Processing Complete",
      description: `Processed ${urlList.length} videos. Check results below.`,
    });
  };

  const handleUseGeneratedBlog = (data: any) => {
    onBlogGenerated(data);
    toast({
      title: "Blog Loaded",
      description: "The generated blog has been loaded into the form",
    });
  };

  const progress = results.length > 0
    ? (results.filter((r) => r.status === 'success' || r.status === 'error').length /
        results.length) *
      100
    : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          Batch Blog Generator
        </CardTitle>
        <CardDescription>
          Generate multiple blog posts from YouTube videos at once (max 10 URLs)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="batch-urls">YouTube URLs (one per line)</Label>
          <Textarea
            id="batch-urls"
            placeholder="https://www.youtube.com/watch?v=example1&#10;https://www.youtube.com/watch?v=example2&#10;https://www.youtube.com/watch?v=example3"
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
            rows={8}
            disabled={isProcessing}
          />
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="generate-images"
            checked={generateImages}
            onCheckedChange={setGenerateImages}
            disabled={isProcessing}
          />
          <Label htmlFor="generate-images" className="cursor-pointer">
            Generate custom AI images for each post
          </Label>
        </div>

        <Button
          onClick={handleBatchGenerate}
          disabled={isProcessing || !urls.trim()}
          className="w-full"
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              Generate All Blogs
            </>
          )}
        </Button>

        {results.length > 0 && (
          <div className="space-y-4 mt-6">
            <div className="space-y-2">
              <Label>Progress</Label>
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground">
                {results.filter((r) => r.status === 'success').length} of {results.length} completed
              </p>
            </div>

            <div className="space-y-2">
              <Label>Results</Label>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {results.map((result, idx) => (
                  <Card key={idx}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{result.url}</p>
                          {result.status === 'pending' && (
                            <p className="text-sm text-muted-foreground">Waiting...</p>
                          )}
                          {result.status === 'processing' && (
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Processing...
                            </p>
                          )}
                          {result.status === 'success' && (
                            <div className="space-y-2">
                              <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-2">
                                <CheckCircle2 className="h-3 w-3" />
                                Success
                              </p>
                              {result.data && (
                                <p className="text-sm font-medium">{result.data.title}</p>
                              )}
                            </div>
                          )}
                          {result.status === 'error' && (
                            <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                              <XCircle className="h-3 w-3" />
                              {result.error || 'Failed'}
                            </p>
                          )}
                        </div>
                        {result.status === 'success' && result.data && (
                          <Button
                            size="sm"
                            onClick={() => handleUseGeneratedBlog(result.data)}
                          >
                            Use This
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
